import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, CreditCard, Users, Search, Settings, Package, Clipboard, MessageSquare, Briefcase, Shield, QrCode } from 'lucide-react';

import { AdminMetrics } from './AdminMetrics';
import { KPIDashboardWithChart } from './KPIDashboard';
import { supabase } from '../../lib/supabase';
import { decodeJWTPayload } from '../../utils/jwt';

// Type for backend user profile
interface BackendUserProfile {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  is_admin: boolean;
  is_developer: boolean;
}

function BackendDashboardContent() {
  const navigate = useNavigate();
  const [user, setUser] = useState<BackendUserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          return;
        }

        // Check JWT claims for admin/developer status
        const claims = session.access_token ? decodeJWTPayload(session.access_token) : null;
        if (!claims) {
          console.error('[BackendDashboard] Failed to decode JWT token');
          return;
        }
        const isAdmin = claims.is_admin === true;
        const isDeveloper = claims.is_developer === true;

        // Get user profile
        const { data: userProfile } = await supabase
          .from('user_profiles')
          .select('id, email, first_name, last_name')
          .eq('id', session.user.id)
          .single();

        if (userProfile) {
          setUser({
            id: userProfile.id,
            email: userProfile.email || '',
            first_name: userProfile.first_name,
            last_name: userProfile.last_name,
            is_admin: isAdmin,
            is_developer: isDeveloper,
          });
        }
      } catch (err) {
        console.error('[BackendDashboard] Failed to load user data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111010] text-[#EBFFEE] flex items-center justify-center font-['Inter']">
        <div className="text-[#A9A3A3]">Loading backend dashboard...</div>
      </div>
    );
  }

  const userDisplayName = user?.first_name && user?.last_name
    ? `${user.first_name} ${user.last_name}`
    : user?.email || 'User';

  const userRoles = [];
  if (user?.is_admin) userRoles.push('Admin');
  if (user?.is_developer) userRoles.push('Developer');

  return (
    <div className="w-full max-w-[100vw] mx-auto px-4 sm:px-5 md:px-6 lg:px-7 xl:px-8 2xl:px-10 pt-2 sm:pt-3 md:pt-4 lg:pt-5 xl:pt-6 pb-8">
        {/* KPI Dashboard */}
        <section className="mb-8">
          <h2 className="text-[#EBFFEE] text-2xl font-bold mb-6">Key Performance Indicators</h2>
          <KPIDashboardWithChart />
        </section>

        {/* Metrics Dashboard */}
        <section className="mb-8">
          <h2 className="text-[#EBFFEE] text-2xl font-bold mb-6">Business Metrics Overview</h2>
          <AdminMetrics />
        </section>

        {/* Backend Tools */}
        <section className="mb-6">
          <h2 className="text-[#EBFFEE] text-xl font-semibold mb-4">Backend Tools</h2>
          <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg overflow-hidden">
            {/* Admin Tools */}
            {user?.is_admin && (
              <>
                <button
                  onClick={() => navigate('/admin/stripe-management')}
                  className="w-full text-left px-6 py-4 border-b border-[#504A4A] hover:bg-[#363232] hover:text-text-primary flex justify-between items-center transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-5 h-5 text-rollops-accent" />
                    <span className="text-[#EBFFEE] font-medium">Stripe Management</span>
                  </div>
                  <span className="text-[#A9A3A3] text-sm">Admin</span>
                </button>
                <button
                  onClick={() => navigate('/admin/rollops-subscriptions')}
                  className="w-full text-left px-6 py-4 border-b border-[#504A4A] hover:bg-[#363232] hover:text-text-primary flex justify-between items-center transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Package className="w-5 h-5 text-text-primary" />
                    <span className="text-[#EBFFEE] font-medium">RollOps Subscriptions</span>
                  </div>
                  <span className="text-[#A9A3A3] text-sm">Admin</span>
                </button>
                <button
                  onClick={() => navigate('/admin/customers')}
                  className="w-full text-left px-6 py-4 border-b border-[#504A4A] hover:bg-[#363232] hover:text-text-primary flex justify-between items-center transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-rollops-accent" />
                    <span className="text-[#EBFFEE] font-medium">Customer Dashboard</span>
                  </div>
                  <span className="text-[#A9A3A3] text-sm">Admin</span>
                </button>
                <button
                  onClick={() => navigate('/admin/jobs')}
                  className="w-full text-left px-6 py-4 border-b border-[#504A4A] hover:bg-[#363232] hover:text-text-primary flex justify-between items-center transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Briefcase className="w-5 h-5 text-rollops-accent" />
                    <span className="text-[#EBFFEE] font-medium">Job Board</span>
                  </div>
                  <span className="text-[#A9A3A3] text-sm">Admin</span>
                </button>
                <button
                  onClick={() => navigate('/admin/admin-management')}
                  className="w-full text-left px-6 py-4 border-b border-[#504A4A] hover:bg-[#363232] hover:text-text-primary flex justify-between items-center transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-rollops-accent" />
                    <span className="text-[#EBFFEE] font-medium">Admin Access</span>
                  </div>
                  <span className="text-[#A9A3A3] text-sm">Admin</span>
                </button>
                <button
                  onClick={() => navigate('/admin/qr-generator')}
                  className="w-full text-left px-6 py-4 border-b border-[#504A4A] hover:bg-[#363232] hover:text-text-primary flex justify-between items-center transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <QrCode className="w-5 h-5 text-rollops-accent" />
                    <span className="text-[#EBFFEE] font-medium">Route QR Generator</span>
                  </div>
                  <span className="text-[#A9A3A3] text-sm">Admin</span>
                </button>
                <button
                  onClick={() => navigate('/admin/survey-builder?type=initial_signup')}
                  className="w-full text-left px-6 py-4 border-b border-[#504A4A] hover:bg-[#363232] hover:text-text-primary flex justify-between items-center transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Clipboard className="w-5 h-5 text-text-primary" />
                    <span className="text-[#EBFFEE] font-medium">Survey Builder</span>
                  </div>
                  <span className="text-[#A9A3A3] text-sm">Admin</span>
                </button>
                <button
                  onClick={() => navigate('/admin/support-requests')}
                  className="w-full text-left px-6 py-4 border-b border-[#504A4A] hover:bg-[#363232] hover:text-text-primary flex justify-between items-center transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-5 h-5 text-text-primary" />
                    <span className="text-[#EBFFEE] font-medium">Support Requests</span>
                  </div>
                  <span className="text-[#A9A3A3] text-sm">Admin</span>
                </button>
              </>
            )}

            {/* Developer Tools */}
            {user?.is_developer && (
              <>
                <button
                  onClick={() => navigate('/dev/companies')}
                  className="w-full text-left px-6 py-4 border-b border-[#504A4A] hover:bg-[#363232] hover:text-text-primary flex justify-between items-center transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-rollops-accent" />
                    <span className="text-[#EBFFEE] font-medium">Company Search</span>
                  </div>
                  <span className="text-[#A9A3A3] text-sm">Developer</span>
                </button>
                <button
                  onClick={() => navigate('/dev/impersonate')}
                  className="w-full text-left px-6 py-4 border-b border-[#504A4A] hover:bg-[#363232] hover:text-text-primary flex justify-between items-center transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-text-primary" />
                    <span className="text-[#EBFFEE] font-medium">User Impersonation</span>
                  </div>
                  <span className="text-[#A9A3A3] text-sm">Developer</span>
                </button>
              </>
            )}

            {/* Coming Soon Tools */}
            <button
              disabled
              className="w-full text-left px-6 py-4 border-b border-[#504A4A] text-[#706A6A] cursor-not-allowed flex justify-between items-center"
            >
              <div className="flex items-center gap-3">
                <Settings className="w-5 h-5" />
                <span className="font-medium">Audit Logs</span>
              </div>
              <span className="text-sm">Coming Soon</span>
            </button>
            <button
              disabled
              className="w-full text-left px-6 py-4 border-b border-[#504A4A] text-[#706A6A] cursor-not-allowed flex justify-between items-center"
            >
              <div className="flex items-center gap-3">
                <Search className="w-5 h-5" />
                <span className="font-medium">System Health</span>
              </div>
              <span className="text-sm">Coming Soon</span>
            </button>
            <button
              disabled
              className="w-full text-left px-6 py-4 text-[#706A6A] cursor-not-allowed flex justify-between items-center"
            >
              <div className="flex items-center gap-3">
                <Settings className="w-5 h-5" />
                <span className="font-medium">Database Console</span>
              </div>
              <span className="text-sm">Coming Soon</span>
            </button>
          </div>
        </section>

        {/* User Profile */}
        <section className="mb-6">
          <h2 className="text-[#EBFFEE] text-xl font-semibold mb-4">User Profile</h2>
          <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg overflow-hidden">
            <div className="divide-y divide-[#504A4A]">
              <div className="px-6 py-3 flex justify-between">
                <span className="text-[#A9A3A3]">ID</span>
                <span className="text-[#EBFFEE] font-mono text-sm">{user?.id}</span>
              </div>
              <div className="px-6 py-3 flex justify-between">
                <span className="text-[#A9A3A3]">Email</span>
                <span className="text-[#EBFFEE]">{user?.email}</span>
              </div>
              <div className="px-6 py-3 flex justify-between">
                <span className="text-[#A9A3A3]">Name</span>
                <span className="text-[#EBFFEE]">{userDisplayName}</span>
              </div>
              <div className="px-6 py-3 flex justify-between">
                <span className="text-[#A9A3A3]">Roles</span>
                <span className="text-[#EBFFEE]">{userRoles.join(', ') || 'None'}</span>
              </div>
            </div>
          </div>
        </section>
    </div>
  );
}

// Export the content component directly since AdminLayout is provided by the route
export default function BackendDashboard() {
  return <BackendDashboardContent />;
}

